
import { useState, useEffect } from 'react';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip,
  ReferenceLine,
  LineChart,
  Line 
} from 'recharts';
import { 
  ArrowUpRight, 
  ArrowDownRight,
  TrendingUp,
  TrendingDown,
  Info
} from 'lucide-react';
import { StockData, StockDataPoint } from '../data/sampleData';
import { formatPercentChange, formatCurrency, formatDate, calculateMA, calculateRSI } from '../utils/stockUtils';
import { cn } from '@/lib/utils';

interface StockChartProps {
  stockData: StockData;
  showPrediction?: boolean;
  height?: number;
  minimal?: boolean;
  externalTimeframe?: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All';
  onTimeframeChange?: (timeframe: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All') => void;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isPrediction = data.isPrediction;

    return (
      <div className="glass p-4 rounded-lg shadow-lg border border-border/30 min-w-[200px] animate-fade-in">
        <div className="text-xs font-medium text-muted-foreground mb-1">
          {formatDate(label)}
        </div>
        <div className="text-base font-semibold">
          {formatCurrency(payload[0].value)}
        </div>
        
        {isPrediction && (
          <div className="mt-1 flex items-center">
            <span className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded-full">
              Prediction
            </span>
          </div>
        )}
        
        {data.volume && (
          <div className="mt-2 text-xs text-muted-foreground">
            Volume: {data.volume.toLocaleString()}
          </div>
        )}
        
        {payload.length > 1 && payload[1].value && (
          <div className="mt-1 text-xs font-medium text-blue-500">
            MA: {payload[1].value.toFixed(2)}
          </div>
        )}
      </div>
    );
  }

  return null;
};

const StockChart = ({ 
  stockData, 
  showPrediction = true, 
  height = 400, 
  minimal = false,
  externalTimeframe,
  onTimeframeChange
}: StockChartProps) => {
  const [timeframe, setTimeframe] = useState<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'>(externalTimeframe || '3M');
  const [showIndicators, setShowIndicators] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  
  // Update local timeframe if external timeframe changes
  useEffect(() => {
    if (externalTimeframe) {
      setTimeframe(externalTimeframe);
    }
  }, [externalTimeframe]);
  
  // Handle timeframe change
  const handleTimeframeChange = (newTimeframe: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All') => {
    setTimeframe(newTimeframe);
    if (onTimeframeChange) {
      onTimeframeChange(newTimeframe);
    }
  };
  
  // Prepare data based on selected timeframe
  useEffect(() => {
    const prepareData = () => {
      // Get the stock data points
      const points = [...stockData.data];
      
      // Filter based on timeframe
      let filteredPoints;
      const now = new Date();
      
      switch (timeframe) {
        case '1W':
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          filteredPoints = points.filter(p => new Date(p.date) >= oneWeekAgo);
          break;
        case '1M':
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          filteredPoints = points.filter(p => new Date(p.date) >= oneMonthAgo);
          break;
        case '3M':
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          filteredPoints = points.filter(p => new Date(p.date) >= threeMonthsAgo);
          break;
        case '6M':
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          filteredPoints = points.filter(p => new Date(p.date) >= sixMonthsAgo);
          break;
        case '1Y':
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          filteredPoints = points.filter(p => new Date(p.date) >= oneYearAgo);
          break;
        case 'All':
        default:
          filteredPoints = points;
      }
      
      // Ensure we have some data points
      if (filteredPoints.length === 0 && points.length > 0) {
        // If filtered data is empty, use at least the most recent points
        filteredPoints = points.slice(-10);
      }
      
      // Calculate indicators if enabled
      const ma20 = calculateMA(filteredPoints, 20);
      
      // Combine data
      const combinedData = filteredPoints.map((point, index) => ({
        ...point,
        ma20: ma20[index]?.value || null,
        isPrediction: false
      }));
      
      // Add prediction data if enabled
      if (showPrediction && stockData.prediction && stockData.prediction.length > 0) {
        const predictionData = stockData.prediction.map(point => ({
          ...point,
          isPrediction: true
        }));
        
        return [...combinedData, ...predictionData];
      }
      
      return combinedData;
    };
    
    const data = prepareData();
    setChartData(data);
  }, [stockData, timeframe, showPrediction, showIndicators]);
  
  const isPositive = stockData.changePercent >= 0;
  
  if (!stockData || !chartData.length) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-muted/20 rounded-lg animate-pulse">
        <span className="text-muted-foreground">Loading chart data...</span>
      </div>
    );
  }

  return (
    <div className={cn(
      "w-full bg-card border rounded-xl overflow-hidden transition-all duration-300 premium-card",
      minimal ? "p-4" : "p-6"
    )}>
      {!minimal && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-semibold">{stockData.name}</h3>
              <span className="text-sm font-medium text-muted-foreground">{stockData.symbol}</span>
            </div>
            
            <div className="flex items-center mt-1">
              <span className="text-xl font-bold">{formatCurrency(stockData.currentValue)}</span>
              <div className={cn(
                "ml-2 flex items-center px-2 py-0.5 rounded-full text-sm font-medium",
                isPositive ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50"
              )}>
                {isPositive ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                <span className="ml-1">{formatPercentChange(stockData.changePercent)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-1">
            {(['1W', '1M', '3M', '6M', '1Y', 'All'] as const).map((option) => (
              <button
                key={option}
                onClick={() => handleTimeframeChange(option)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-all",
                  timeframe === option 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-transparent text-muted-foreground hover:bg-muted"
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className={cn("overflow-hidden", minimal ? "h-[120px]" : `h-[${height}px]`)}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            {!minimal && (
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false} 
                stroke="rgba(0,0,0,0.05)" 
              />
            )}
            
            <XAxis 
              dataKey="date" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10 }}
              minTickGap={30}
              tickFormatter={(date) => {
                if (timeframe === '1W' || timeframe === '1M') {
                  return new Date(date).toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
                }
                return new Date(date).toLocaleDateString('en-US', { month: 'short' });
              }}
              hide={minimal}
            />
            
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10 }}
              domain={['auto', 'auto']}
              tickFormatter={(value) => `$${value}`}
              hide={minimal}
            />
            
            {!minimal && <Tooltip content={<CustomTooltip />} />}
            
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop 
                  offset="5%" 
                  stopColor={isPositive ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)"} 
                  stopOpacity={0.8}
                />
                <stop 
                  offset="95%" 
                  stopColor={isPositive ? "rgba(34, 197, 94, 0)" : "rgba(239, 68, 68, 0)"} 
                  stopOpacity={0}
                />
              </linearGradient>
              
              <linearGradient id="colorPrediction" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgba(79, 70, 229, 0.3)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="rgba(79, 70, 229, 0)" stopOpacity={0} />
              </linearGradient>
            </defs>
            
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke={isPositive ? "rgb(34, 197, 94)" : "rgb(239, 68, 68)"}
              strokeWidth={1.5}
              fillOpacity={1}
              fill="url(#colorValue)" 
              activeDot={{ r: 6 }}
              dot={false}
            />
            
            {showIndicators && (
              <Line 
                type="monotone" 
                dataKey="ma20" 
                stroke="rgba(99, 102, 241, 0.8)" 
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="5 5"
              />
            )}
            
            {!minimal && chartData[0] && (
              <ReferenceLine 
                y={chartData[0].value} 
                stroke="rgba(0,0,0,0.1)" 
                strokeDasharray="3 3" 
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {minimal && (
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-sm font-medium">{stockData.symbol}</span>
          </div>
          <div className={cn(
            "flex items-center",
            isPositive ? "text-green-600" : "text-red-600"
          )}>
            {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span className="ml-1 text-xs font-medium">
              {formatPercentChange(stockData.changePercent)}
            </span>
          </div>
        </div>
      )}
      
      {!minimal && showPrediction && (
        <div className="mt-4 flex items-center">
          <div className="flex items-center px-3 py-1.5 rounded-full bg-blue-50 text-blue-600">
            <Info size={14} />
            <span className="ml-1.5 text-xs font-medium">LSTM Prediction (30 days)</span>
          </div>
          <div className="ml-auto text-xs text-muted-foreground">
            <button
              onClick={() => setShowIndicators(!showIndicators)}
              className="underline hover:text-foreground transition-colors"
            >
              {showIndicators ? 'Hide' : 'Show'} MA(20)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockChart;
